<template>
  <el-dialog
    v-model="dialogVisible"
    :title="''"
    width="600px"
    :close-on-click-modal="true"
    :close-on-press-escape="true"
    :show-close="false"
    class="personal-settings-modal"
    @close="handleClose"
  >
    <div class="modal-header">
      <div class="header-title">账号设置</div>
      <div class="header-close" @click="handleClose">
        <img :src="images.close3" alt="" srcset="" />
      </div>
    </div>

    <!-- 基本信息部分 -->
    <div class="section-header">
      <span class="section-title">基本信息</span>
      <el-button class="write-off-btn" type="default" size="small" @click="goToWriteOff">
        去注销
      </el-button>
    </div>

    <!-- 头像 -->
    <div class="form-item form-avatar">
      <div class="form-label">头像</div>
      <div class="avatar-wrapper" @click="handleAvatarClick">
        <img :src="avatarSrc" alt="头像" class="avatar-image" />
        <div class="avatar-edit">
          <img :src="images.editWhite" alt="" />
        </div>
        <input
          ref="avatarInputRef"
          type="file"
          accept="image/*"
          style="display: none"
          @change="handleAvatarChange"
        />
      </div>
    </div>

    <!-- 账号名：只读展示，取自登录手机号 -->
    <div class="form-item form-account">
      <div class="form-label">账号名</div>
      <div class="account-name">{{ accountName }}</div>
    </div>

    <!-- 昵称 -->
    <div class="form-item">
      <div class="form-label">昵称</div>
      <div class="form-value">
        <el-input
          v-model="editingData.nickname"
          :maxlength="NICKNAME_MAX_LENGTH"
          placeholder="请输入昵称"
          class="nickname-input"
          show-word-limit
        />
      </div>
    </div>

    <!-- 个人简介 -->
    <div class="form-item">
      <div class="form-label">个人简介</div>
      <div class="form-value description-textarea">
        <textarea
          id="textarea"
          v-model="editingData.intro"
          name="textarea"
          placeholder="请输入您的个人简介"
          :maxlength="INTRO_MAX_LENGTH"
          class="description-input"
          style="resize: none"
        />
        <!-- 右侧字数统计和清空 -->
        <div class="flex-end">
          <div class="footer-right" @click="editingData.intro = ''">
            <span class="char-count">{{ editingData.intro.length }}/{{ INTRO_MAX_LENGTH }}</span>
            <img class="clear-img" :src="images.clear2" alt="清空" />
          </div>
        </div>
      </div>

      <div class="modal-footer">
        <el-button type="primary" class="save-btn" :loading="saving" @click="handleSave">
          保存
        </el-button>
      </div>
    </div>
  </el-dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { ElMessage } from 'element-plus'
import { useUserStore } from '@/stores/user'
import { useUserProfileStore } from '@/stores/userProfile'
import { useModalStore } from '@/stores/modal'
import {
  userApi,
  USER_PROFILE_MAIN_AUDIT_FAILED,
  USER_PROFILE_MAIN_AUDIT_PENDING,
  USER_PROFILE_MAIN_AUDIT_REVIEWING,
  type UpdateUserProfileParams,
  type UpdateUserProfileData,
} from '@/api/user'
import { uploadApi } from '@/api/upload'
import { images } from '@/assets'
import { resolveAvatarUrl } from '@/utils/avatar'

interface Props {
  modelValue: boolean
}

const props = defineProps<Props>()
const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  saved: []
}>()

const NICKNAME_MAX_LENGTH = 20
const INTRO_MAX_LENGTH = 300

const userStore = useUserStore()
const userProfileStore = useUserProfileStore()

const dialogVisible = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
})

const avatarInputRef = ref<HTMLInputElement>()
const saving = ref(false)

const editingData = ref({
  avatar: '',
  nickname: '',
  intro: '',
})

const avatarSrc = computed(() => resolveAvatarUrl(editingData.value.avatar, images.avatar))

/** 账号名只读，展示登录手机号 */
const accountName = computed(() => userStore.userInfo?.phone || '')

const getV1ErrorMessage = (error: unknown, fallback: string) => {
  if (error && typeof error === 'object') {
    const err = error as { response?: { data?: { message?: string } }; message?: string }
    const message = err.response?.data?.message || err.message
    if (typeof message === 'string' && message.trim() && message !== 'Not Authenticated') {
      return message
    }
  }
  return fallback
}

const syncEditingData = () => {
  editingData.value = {
    avatar: userProfileStore.user.avatar || '',
    nickname: userProfileStore.user.nickname || '',
    intro: userProfileStore.user.intro || '',
  }
}

const profileSaveMessage = (data?: UpdateUserProfileData) => {
  const audits = data?.audits || []
  if (data?.noChange || audits.length === 0) {
    return '保存成功'
  }
  if (audits.some((item) => item.mainAuditStatus === USER_PROFILE_MAIN_AUDIT_FAILED)) {
    return '部分资料未通过审核'
  }
  if (
    audits.some(
      (item) =>
        item.mainAuditStatus === USER_PROFILE_MAIN_AUDIT_PENDING ||
        item.mainAuditStatus === USER_PROFILE_MAIN_AUDIT_REVIEWING
    )
  ) {
    return '资料已提交，审核通过后生效'
  }
  return '保存成功'
}

watch(
  () => props.modelValue,
  async (open) => {
    if (!open) return
    await userProfileStore.fetchDetail()
    syncEditingData()
  },
  { immediate: true }
)

const handleAvatarClick = () => {
  avatarInputRef.value?.click()
}

const handleAvatarChange = async (event: Event) => {
  const target = event.target as HTMLInputElement
  const file = target.files?.[0]
  if (!file) return

  try {
    const result = await uploadApi.uploadMediaResource(file, {
      showLoading: true,
      loadingText: '头像上传中...',
    })

    if (result.success && result.url) {
      editingData.value.avatar = result.url
    }
  } catch (error) {
    console.error('上传头像失败:', error)
  } finally {
    if (target) {
      target.value = ''
    }
  }
}

const handleSave = async () => {
  if (!userStore.isLoggedIn) {
    ElMessage.warning('请先登录')
    return
  }

  const nickname = editingData.value.nickname.trim()
  const intro = editingData.value.intro.trim()
  const avatar = editingData.value.avatar.trim()

  if (!nickname) {
    ElMessage.warning('请输入昵称')
    return
  }

  const payload: UpdateUserProfileParams = { nickname }
  if (avatar) payload.avatar = avatar
  if (intro) payload.intro = intro

  if (saving.value) return
  saving.value = true

  try {
    const res = await userApi.updateProfile(payload)
    if (res.code === 0) {
      ElMessage.success(profileSaveMessage(res.data) || res.message || '保存成功')
      await userProfileStore.fetchDetail()
      emit('saved')
      handleClose()
    } else {
      ElMessage.error(res.message || '保存失败')
    }
  } catch (error) {
    console.error('保存失败:', error)
    ElMessage.error(getV1ErrorMessage(error, '保存失败，请重试'))
  } finally {
    saving.value = false
  }
}

// 去注销
const goToWriteOff = () => {
  const modalStore = useModalStore()
  // 先关闭账号设置弹窗
  modalStore.closePersonalSettingsModal()
  // 然后打开注销弹窗
  modalStore.openWriteOffModal()
}

const handleClose = () => {
  dialogVisible.value = false
}
</script>

<style lang="scss" scoped>
.personal-settings-modal {
  position: relative;

  .header-title {
    margin-bottom: 11px;
    font-size: var(--font-4xl);
    font-weight: bold;
    color: var(--text-primary);
  }

  .header-close {
    position: absolute;
    top: 32px;
    right: 32px;
    width: 32px;
    height: 32px;
    cursor: pointer;
    img {
      width: 100%;
      height: 100%;
    }
  }

  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 25px;

    .section-title {
      font-size: var(--font-xxl);
      font-weight: 600;
      color: var(--text-sidebar);
    }

    .write-off-btn {
      height: 32px;
      padding: 4px 16px !important;
      background-color: rgba(63, 56, 71, 0.45) !important;
      border: 1px solid rgba(255, 255, 255, 0.18) !important;
      border-radius: 8px !important;
      color: var(--text-content) !important;
      font-size: var(--font-md) !important;
    }
  }

  .form-item {
    margin-bottom: var(--spacing-xxl);

    .form-label {
      color: var(--text-content);
      font-size: var(--font-lg);
      margin-bottom: var(--spacing-md);
    }
    .form-value {
      border-radius: var(--spacing-sm);
      background: var(--bg-card);
      font-size: var(--font-lg);
      :deep(.el-input__wrapper) {
        padding: 0 17px;
      }
      :deep(.el-input__count-inner) {
        color: var(--text-hui) !important;
      }
    }
    .description-textarea {
      padding-bottom: 11px;
      .description-input {
        width: 100%;
        height: 110px;
        padding: 11px 17px;
        background: transparent;
        border: none;
        outline: none;
        color: var(--text-primary);
        resize: none;
        font-size: var(--font-lg);
        font-family: inherit;

        &:focus {
          outline: none;
        }

        &::placeholder {
          color: var(--text-six) !important;
        }
      }

      .footer-right {
        display: flex;
        align-items: center;
        gap: var(--spacing-xs);
        margin-right: 13px;

        .char-count {
          font-size: var(--font-lg);
          color: var(--text-hui);
        }

        .clear-img {
          width: 26px;
          height: 26px;
          cursor: pointer;
        }
      }
    }

    .nickname-input {
      height: 50px;
    }
  }

  .form-account {
    display: flex;
    align-items: center;
    gap: var(--spacing-md);

    .form-label {
      margin-bottom: 0;
      flex-shrink: 0;
    }

    .account-name {
      color: var(--text-primary);
      font-size: var(--font-lg);
      line-height: 1.4;
      user-select: text;
    }
  }

  .form-avatar {
    display: flex;
    align-items: center;

    .form-label {
      margin-bottom: 0;
    }

    .avatar-wrapper {
      position: relative;
      cursor: pointer;
      margin-left: 48px;

      .avatar-image {
        width: 80px;
        height: 80px;
        border-radius: 50%;
        object-fit: cover;
      }

      .avatar-edit {
        position: absolute;
        bottom: 0;
        right: 0;
        width: 26px;
        height: 26px;
        background: var(--primary-color);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        img {
          width: 16px;
          height: 16px;
        }
      }
    }
  }

  .modal-footer {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 35px;

    .save-btn {
      width: 204px;
      height: 50px;
      border: none;
      border-radius: 25px;
      background: linear-gradient(
        90deg,
        rgba(204, 166, 244, 1) 0%,
        rgba(192, 126, 255, 1) 53%,
        rgba(204, 166, 244, 1) 99%
      );
      font-size: var(--font-lg);
      color: var(--text-primary);
    }
  }
}
</style>

<style lang="scss">
.el-dialog.personal-settings-modal {
  .el-dialog__header {
    display: none;
  }

  .el-dialog__body {
    background: #100520 100% !important;
    border-radius: var(--radius-lg) !important;
    padding: 45px var(--spacing-4xl) !important;
  }
}
</style>
